import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { LanguageUtils, TextWithTooltip } from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import {
  LabelValue,
  type LabelValueLayout,
} from '~/components/common/labels/LabelValue'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import InventoryOrderItemStatusLabel from '../InventoryOrderItemStatusLabel'
import { OrderItemFlags } from '../order-items/OrderItemFlags'
import { OrderCard } from '../OrderCard'
import {
  getInventoryItemDisplayName,
  getInventoryItemDisplayQuantity,
} from '../utils/inventoryItemUtils'

import styles from './OrderItemReceiptsPageHeader.module.scss'

interface OrderItemReceiptsPageHeaderProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPageHeader = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageHeaderProps) => {
  const { t } = useTranslation()

  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)

  const productSizeUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const packageTypeName = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageType.id,
    PackageType,
  )

  const { orderedQuantity, perPackageAmount } = inventoryOrderItem
  const orderQuantity = getInventoryItemDisplayQuantity({
    orderedQuantity,
    perPackageAmount,
    productSizeUnit,
    packageType: packageTypeName,
  })

  const orderItemLabelValueProps = {
    LabelProps: { className: styles.label },
    ValueProps: { className: styles.value },
    layout: 'vertical' as LabelValueLayout,
  }

  return (
    <div className={styles.pageHeader}>
      <div>
        <OrderCard readOnly inventoryOrder={inventoryOrder} />
      </div>
      <div className={styles.itemContainer}>
        <div className={styles.itemHeader}>
          <div>
            <span className={styles.title}>
              {getInventoryItemDisplayName(inventoryOrderItem.inventoryItem)}
            </span>
            <InventoryOrderItemStatusLabel
              statusId={inventoryOrderItem.status.id}
            />
            <OrderItemFlags
              controlled={inventoryOrderItem.inventoryItem.controlled}
              storeCold={
                inventoryOrderItem.inventoryItem.variation?.storeCold ?? false
              }
            />
          </div>
          <TextWithTooltip
            className={styles.supplierName}
            tooltipText={t('Admin:CATALOG.ORDERS.ORDER_ITEM_SUPPLIER_NAME')}
          >
            {inventoryOrderItem.externalItemName}
          </TextWithTooltip>
        </div>

        <div className={styles.itemBody}>
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.QUANTITY_ORDERED_LABEL')}
            value={
              <>
                <span className={styles.highlight}>{orderQuantity.total}</span>
                {orderQuantity.breakdown && (
                  <>
                    {' '}
                    <span>({orderQuantity.breakdown})</span>
                  </>
                )}
              </>
            }
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.MANUFACTURER_LABEL')}
            value={inventoryOrderItem.manufacturer.name}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.UPC_LABEL')}
            value={inventoryOrderItem.upc || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.NDC_LABEL')}
            value={inventoryOrderItem.ndc || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.INTERNAL_PART_NUMBER_LABEL')}
            value={inventoryOrderItem.inventoryItem.variation?.cumulusId || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.SUPPLIER_SKU_LABEL')}
            value={inventoryOrderItem.sku}
          />
        </div>
      </div>
    </div>
  )
}
